import { Input, InputProps } from "antd";
import React, { useEffect, useRef } from "react";

export const formatTaxId = (value: string) => {
  if (!value) return value;
  const digits = value.replace(/\D/g, "").slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return digits
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

export const isValidCPF = (cpf: string) => {
  if (!cpf) return false;
  const digits = cpf.replace(/\D/g, "");
  if (digits.length !== 11) return false;
  if (/^(\d)\1+$/.test(digits)) return false;

  let sum = 0;
  let remainder;

  for (let i = 1; i <= 9; i++)
    sum = sum + parseInt(digits.substring(i - 1, i)) * (11 - i);
  remainder = (sum * 10) % 11;

  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(digits.substring(9, 10))) return false;

  sum = 0;
  for (let i = 1; i <= 10; i++)
    sum = sum + parseInt(digits.substring(i - 1, i)) * (12 - i);
  remainder = (sum * 10) % 11;

  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(digits.substring(10, 11))) return false;

  return true;
};

export const isValidCNPJ = (cnpj: string) => {
  if (!cnpj) return false;
  const digits = cnpj.replace(/\D/g, "");
  if (digits.length !== 14) return false;
  if (/^(\d)\1+$/.test(digits)) return false;

  let size = digits.length - 2;
  let numbers = digits.substring(0, size);
  const checkDigits = digits.substring(size);
  let sum = 0;
  let pos = size - 7;
  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(checkDigits.charAt(0))) return false;

  size = size + 1;
  numbers = digits.substring(0, size);
  sum = 0;
  pos = size - 7;
  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(checkDigits.charAt(1))) return false;

  return true;
};

export const MaskedTaxIdInput: React.FC<InputProps> = (props) => {
  const inputRef = useRef<any>(null);
  const selectionRef = useRef<number | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const originalValue = input.value;
    const selectionStart = input.selectionStart || 0;

    // Count digits before the cursor
    const digitsBeforeCursor = originalValue
      .slice(0, selectionStart)
      .replace(/\D/g, "").length;

    const maskedValue = formatTaxId(originalValue);

    // Find new cursor position
    let newSelectionStart = 0;
    let digitsCount = 0;
    for (
      let i = 0;
      i < maskedValue.length && digitsCount < digitsBeforeCursor;
      i++
    ) {
      if (/\d/.test(maskedValue[i])) {
        digitsCount++;
      }
      newSelectionStart = i + 1;
    }

    selectionRef.current = newSelectionStart;

    if (props.onChange) {
      // Create a shallow copy of the event or just pass the modified value
      // Form.Item expects the value to be updated
      props.onChange(e);
    }
  };

  useEffect(() => {
    if (selectionRef.current !== null && inputRef.current) {
      const input = inputRef.current.input || inputRef.current;
      if (input && input.setSelectionRange) {
        input.setSelectionRange(selectionRef.current, selectionRef.current);
      }
      selectionRef.current = null;
    }
  });

  // Ensure value passed to Input is formatted
  const value = props.value ? formatTaxId(String(props.value)) : props.value;

  return (
    <Input {...props} value={value} ref={inputRef} onChange={handleChange} />
  );
};
